import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { QUERY_KEYS } from '@/constants';
import { warehouseApi } from '@/apis/warehouse';
import type {
  UpdateAssignmentRequest,
  VerifyItemsRequest,
  ReportIssueRequest,
  AddWarehouseNoteRequest,
  AddInboundTrackingRequest,
  UpdateInboundTrackingRequest,
  NotifyCustomerRequest,
  AddPackageRequest,
  ShippingDocumentType,
  WarehouseOrderDetailResponse,
} from '@/apis/warehouse';
import { adminOrdersApi } from '@/apis/orders';
import { usersApi } from '@/apis/users';

export const useWarehouseKanban = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.WAREHOUSE, 'kanban'],
    queryFn: () => warehouseApi.getKanban(),
  });
};

/** Powers both the quick-view drawer and the full detail page — same backend endpoint. */
export const useWarehouseOrderDetail = (orderId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.WAREHOUSE, 'order', orderId],
    queryFn: () => warehouseApi.getOrderDetail(orderId),
    enabled: !!orderId,
  });
};

/** Real dedicated endpoint — GET /warehouse/handlers, filtered server-side by UserEntity.isWarehouseHandler. */
export const useWarehouseHandlers = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.WAREHOUSE, 'handlers'],
    queryFn: () => warehouseApi.getHandlers(),
    staleTime: 5 * 60 * 1000,
  });
};

/** Resolves raw userIds (e.g. warehouseNotes.author) to real display name + role via /users/:id — never guesses. */
export const useUserSummaries = (userIds: string[]) => {
  const uniqueIds = [...new Set(userIds)].filter(Boolean);
  const results = useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: [QUERY_KEYS.USERS, id],
      queryFn: () => usersApi.getUserById(id),
      staleTime: 10 * 60 * 1000,
    })),
  });
  const byId = new Map<string, { name: string; roleLabel: string }>();
  uniqueIds.forEach((id, i) => {
    const user = results[i]?.data;
    if (!user) return;
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
    const roleLabel = user.isSalesperson ? 'Sales' : user.userRole === 'admin' ? 'Admin' : 'Staff';
    byId.set(id, { name, roleLabel });
  });
  return byId;
};

/** Manual status change (the dropdown next to "Order Details") — reuses the real Orders endpoint; Order.warehouseStatus is a real, validated column. */
export const useUpdateWarehouseCardStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, warehouseStatus }: { orderId: string; warehouseStatus: string }) =>
      adminOrdersApi.updateOrder(orderId, { warehouseStatus }),
    onSuccess: (_data, { orderId }) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WAREHOUSE, 'kanban'] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WAREHOUSE, 'order', orderId] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to update status');
    },
  });
};

/** Replaces the deprecated per-field handler assignment — updates handler and/or salesperson in one call. */
export const useUpdateAssignment = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateAssignmentRequest) => warehouseApi.updateAssignment(orderId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WAREHOUSE, 'kanban'] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WAREHOUSE, 'order', orderId] });
      toast.success('Assignment updated');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to update assignment');
    },
  });
};

/** Optimistic — avoids a full refetch flash on every checkbox click / qty keystroke. */
export const useVerifyItems = (orderId: string) => {
  const queryClient = useQueryClient();
  const queryKey = [QUERY_KEYS.WAREHOUSE, 'order', orderId];
  return useMutation({
    mutationFn: (data: VerifyItemsRequest) => warehouseApi.verifyItems(orderId, data),
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<WarehouseOrderDetailResponse>(queryKey);
      queryClient.setQueryData<WarehouseOrderDetailResponse>(queryKey, (current) => {
        if (!current) return current;
        return {
          ...current,
          order: {
            ...current.order,
            items: current.order.items?.map((item) => {
              const patch = data.items.find((i) => i.id === item.id);
              return patch ? { ...item, ...patch } : item;
            }),
          },
        };
      });
      return { previous };
    },
    onError: (error: { response?: { data?: { message?: string } } }, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
      toast.error(error.response?.data?.message || 'Failed to update item');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
};

/** Moves the order to the ISSUE column. */
export const useReportIssue = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ReportIssueRequest) => warehouseApi.reportIssue(orderId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WAREHOUSE, 'kanban'] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WAREHOUSE, 'order', orderId] });
      toast.success('Issue reported');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to report issue');
    },
  });
};

export const useAddWarehouseNote = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AddWarehouseNoteRequest) => warehouseApi.addNote(orderId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WAREHOUSE, 'order', orderId] });
      toast.success('Note added');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to add note');
    },
  });
};

export const useAttachWarehouseFiles = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (files: File[]) => warehouseApi.attachFiles(orderId, files),
    onSuccess: (_data, files) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WAREHOUSE, 'order', orderId] });
      toast.success(files.length === 1 ? 'File attached' : `${files.length} files attached`);
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to attach file(s)');
    },
  });
};

export const useDeleteWarehouseFile = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fileId: string) => warehouseApi.deleteFile(orderId, fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WAREHOUSE, 'order', orderId] });
      toast.success('File removed');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to remove file');
    },
  });
};

/** Order status can move AWAITING/ALMOST_OPEN/OPEN_ORDERS as tracking numbers match up, so also refresh the board. Result carries `isMatch` for warehouse-source entries so the caller can play the bell/buzzer sound. */
export const useAddInboundTracking = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AddInboundTrackingRequest) => warehouseApi.addInboundTracking(orderId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WAREHOUSE, 'kanban'] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WAREHOUSE, 'order', orderId] });
      toast.success('Tracking logged');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to log tracking');
    },
  });
};

/** Re-running a match check on edit (e.g. fixing a mis-scanned number) can also change the order's status, so refresh the board too. */
export const useUpdateInboundTracking = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ trackingId, ...data }: UpdateInboundTrackingRequest & { trackingId: string }) =>
      warehouseApi.updateInboundTracking(orderId, trackingId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WAREHOUSE, 'kanban'] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WAREHOUSE, 'order', orderId] });
      toast.success('Tracking updated');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to update tracking');
    },
  });
};

export const useDeleteInboundTracking = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (trackingId: string) => warehouseApi.deleteInboundTracking(orderId, trackingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WAREHOUSE, 'kanban'] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WAREHOUSE, 'order', orderId] });
      toast.success('Tracking removed');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to remove tracking');
    },
  });
};

/** Sends a real email to the customer via the warehouse module's notify endpoint. */
export const useNotifyCustomer = (orderId: string) => {
  return useMutation({
    mutationFn: (data: NotifyCustomerRequest) => warehouseApi.notifyCustomer(orderId, data),
    onSuccess: () => toast.success('Email sent to customer'),
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to send email');
    },
  });
};

export const useAddPackage = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AddPackageRequest) => warehouseApi.addPackage(orderId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WAREHOUSE, 'order', orderId] });
      toast.success('Package added');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to add package');
    },
  });
};

export const useUploadShippingDocument = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ type, file }: { type: ShippingDocumentType; file: File }) =>
      warehouseApi.uploadShippingDocument(orderId, type, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WAREHOUSE, 'order', orderId] });
      toast.success('Document uploaded');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to upload document');
    },
  });
};

export const useDeleteShippingDocument = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (type: ShippingDocumentType) => warehouseApi.deleteShippingDocument(orderId, type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WAREHOUSE, 'order', orderId] });
      toast.success('Document removed');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to remove document');
    },
  });
};

export const useMarkReadyForShipping = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => warehouseApi.markReadyForShipping(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WAREHOUSE, 'kanban'] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WAREHOUSE, 'order', orderId] });
      toast.success('Order marked ready for shipping');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to mark ready for shipping');
    },
  });
};
